import { Badge, DataTable, Text } from '@wapharma/design-system';
import type { CustomerSessionLocation } from './CustomerSessionLocationCard';
import { FriendlyDate } from './FriendlyDate';
import { SessionLocationHoverCell } from './SessionConnectionDetails';
import { SessionRowActions } from './SessionRowActions';

export type StaffSessionRow = CustomerSessionLocation & {
  id: string;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  status: 'live' | 'active' | 'expired' | 'revoked';
  userAgentSummary?: string | null;
  /** True when this refresh token is the caller’s current cookie. */
  isCurrent?: boolean;
};

const STATUS_LABEL: Record<StaffSessionRow['status'], string> = {
  live: 'En ligne',
  active: 'Session active',
  expired: 'Expirée',
  revoked: 'Révoquée',
};

const STATUS_TONE: Record<StaffSessionRow['status'], 'success' | 'info' | 'neutral' | 'warning'> = {
  live: 'success',
  active: 'info',
  expired: 'neutral',
  revoked: 'warning',
};

export type StaffSessionActions = {
  busyId?: string | null;
  onLogout: (row: StaffSessionRow) => void;
  onArchive: (row: StaffSessionRow) => void;
  onDelete: (row: StaffSessionRow) => void;
};

type StaffSessionsPanelProps = {
  sessions: StaffSessionRow[];
  /**
   * Narrow host (Paramètres Compte): no sticky pin —
   * avoids status pin covering « Dernière activité ».
   */
  compact?: boolean;
  /** Own-session actions (Paramètres Compte). Omitted on Utilisateurs detail. */
  actions?: StaffSessionActions;
};

const CORE_COLUMNS = [
  {
    key: 'status',
    label: 'Statut',
    width: '9rem',
    render: (row: StaffSessionRow) => (
      <Badge purpose="type" tone={STATUS_TONE[row.status]}>
        {STATUS_LABEL[row.status]}
      </Badge>
    ),
  },
  {
    key: 'lastSeen',
    label: 'Dernière activité',
    width: '12rem',
    render: (row: StaffSessionRow) =>
      row.lastSeenAt ? (
        <FriendlyDate iso={row.lastSeenAt} />
      ) : (
        <Text size="sm" c="dimmed">
          —
        </Text>
      ),
  },
  {
    key: 'location',
    label: 'Lieu',
    width: '14rem',
    render: (row: StaffSessionRow) => <SessionLocationHoverCell session={row} />,
  },
] as const;

export function StaffSessionsPanel({ sessions, compact = false, actions }: StaffSessionsPanelProps) {
  return (
    <DataTable<StaffSessionRow>
      variant="embedded"
      density="compact"
      pagination={false}
      fixedLayout
      pinStartColumnKey={compact ? undefined : 'status'}
      data={sessions}
      rowKey={(row) => row.id}
      emptyMessage="Aucune session."
      columns={[...CORE_COLUMNS]}
      rowLeadingActions={
        actions
          ? (row) => (
              <SessionRowActions
                row={row}
                busy={actions.busyId === row.id}
                onLogout={() => actions.onLogout(row)}
                onArchive={() => actions.onArchive(row)}
                onDelete={() => actions.onDelete(row)}
              />
            )
          : undefined
      }
    />
  );
}
